import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BTN, Sel, StatusPill, daysTxt, useComp } from "./compUtils";
import { ObligationForm } from "./ObligationForm";

export function CompRegister({ onOpen, tick, refresh }) {
  const m = useComp();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ category: "", family: "", color: "", applicability: "" });
  const [arch, setArch] = useState(false);
  const [form, setForm] = useState(false);
  useEffect(() => { api.get("/compliance/obligations", { params: { include_inactive: arch } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [tick, arch]);
  const list = useMemo(() => (rows || []).filter((o) => (!f.category || o.category === f.category) && (!f.family || o.family === f.family)
    && (!f.color || o.color === f.color) && (!f.applicability || o.applicability === f.applicability)
    && (!q || `${o.ref} ${o.title} ${o.equipment_name || ""} ${o.employee_name || ""}`.toLowerCase().includes(q.toLowerCase()))), [rows, f, q]);
  const canCreate = m.is_manager || m.is_hygiene;
  return (
    <div className="space-y-3" data-testid="comp-register">
      <div className={`${CARD} flex flex-wrap items-center gap-2 p-3`}>
        <div className="relative min-w-[12rem] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><Input data-testid="reg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" className="rounded-full pl-9" /></div>
        <Sel testid="reg-f-category" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={m.categories} empty="Toutes catégories" className="w-44" />
        <Sel testid="reg-f-family" value={f.family} onChange={(v) => setF({ ...f, family: v })} options={m.families} empty="Toutes familles" className="w-44" />
        <Sel testid="reg-f-color" value={f.color} onChange={(v) => setF({ ...f, color: v })} options={{ green: "À jour", orange: "Échéance proche", red: "En retard / NC", grey: "À qualifier / N.A." }} empty="Tous états" className="w-40" />
        <Sel testid="reg-f-appl" value={f.applicability} onChange={(v) => setF({ ...f, applicability: v })} options={m.applicability} empty="Toute applicabilité" className="w-40" />
        <label className="flex items-center gap-1 text-xs text-gray-500"><input type="checkbox" data-testid="reg-archived" checked={arch} onChange={(e) => setArch(e.target.checked)} />Archivées</label>
        {canCreate && <Button data-testid="reg-new" className={BTN} onClick={() => setForm(true)}><Plus className="mr-1 h-4 w-4" />Obligation</Button>}
      </div>
      <p className="text-xs text-gray-400">{list.length} obligation(s){!m.is_manager && !m.is_hygiene ? " — vos tâches affectées" : ""}</p>
      <div className={`${CARD} divide-y divide-gray-50 p-2 sm:p-4`}>
        {rows && list.length === 0 && <p className="p-4 text-sm text-gray-400">Aucune obligation.</p>}
        {list.map((o) => (
          <button type="button" key={o.id} onClick={() => onOpen(o.id)} data-testid={`ob-row-${o.id}`} className="flex w-full flex-wrap items-center justify-between gap-2 px-2 py-3 text-left hover:bg-gray-50">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold"><span className="mr-2 font-mono text-xs text-gray-400">{o.ref}</span>{o.title}</p>
              <p className="truncate text-xs text-gray-400">{m.categories[o.category]} · {m.families[o.family]} · {m.natures[o.nature]} · {m.applicability[o.applicability]}
                {o.equipment_name ? ` · ${o.equipment_name}` : ""}{o.employee_name ? ` · ${o.employee_name}` : ""}{o.responsible_name ? ` · ${o.responsible_name}` : ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
              {o.next_due && <span>{fmtDate(o.next_due)} · {daysTxt(o.days)}</span>}
              <StatusPill ob={o} testid={`ob-status-${o.id}`} />
            </div>
          </button>
        ))}
      </div>
      <ObligationForm open={form} onOpenChange={setForm} onSaved={refresh} />
    </div>
  );
}
